// Parser utility functions for vector and quaternion parsing
import type { Vector3, Quaternion } from "../types/primitives";
import { logger } from "../utils/logger";

/**
 * Parse a space-separated string into a Vector3 array [x, y, z]
 * @param str - Space-separated string of 3 numbers
 * @param def - Default value if parsing fails or str is empty
 * @returns Vector3 array [x, y, z]
 */
export function parseVec3(str: string | null | undefined, def: Vector3 = [0, 0, 0]): Vector3 {
  if (!str) return def;
  try {
    const parsed = str.trim().split(/\s+/).map(Number);
    // Ensure we return exactly 3 elements
    return [parsed[0] || 0, parsed[1] || 0, parsed[2] || 0];
  } catch (error) {
    logger.warn("Failed to parse vector:", str, error);
    return def;
  }
}

/**
 * Normalize a quaternion array [w, x, y, z]
 * @param q - Quaternion array [w, x, y, z]
 * @returns Normalized quaternion array [w, x, y, z]
 */
function normalizeQuaternion(q: Quaternion): Quaternion {
  const [w, x, y, z] = q;
  const magnitude = Math.sqrt(w * w + x * x + y * y + z * z);
  if (magnitude < 1e-10) {
    // Return identity quaternion if magnitude is too small
    return [1, 0, 0, 0];
  }
  return [w / magnitude, x / magnitude, y / magnitude, z / magnitude];
}

/**
 * Parse a space-separated string into a Quaternion array [w, x, y, z]
 * MuJoCo stores quaternions as [w, x, y, z]
 * @param str - Space-separated string of 4 numbers (w, x, y, z)
 * @returns Normalized quaternion array [w, x, y, z], or [1, 0, 0, 0] if parsing fails
 */
export function parseQuat(str: string | null | undefined): Quaternion {
  if (!str) return [1, 0, 0, 0];
  try {
    const [w, x, y, z] = str.trim().split(/\s+/).map(Number);
    return normalizeQuaternion([w || 1, x || 0, y || 0, z || 0]);
  } catch (error) {
    logger.warn("Failed to parse quaternion:", str, error);
    return [1, 0, 0, 0];
  }
}
